import { useQuery } from '@tanstack/react-query';
import {
  BandChart,
  EmptyState,
  ErrorNotice,
  HeatmapChart,
  LoadingState,
  Panel,
  StatTile,
  TrendChart,
  type BandPoint,
  type TrendPoint,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { LIVE_REFRESH_MS } from '@/lib/report-window';
import { readLeadTiming, type LeadTiming } from '@/lib/leads';

/** The hours the charts show — leads before 7 AM or after 7 PM Eastern are a rounding error. */
const FIRST_HOUR = 7;
const LAST_HOUR = 19;
const HOURS = Array.from({ length: LAST_HOUR - FIRST_HOUR + 1 }, (_, i) => FIRST_HOUR + i);
/** Monday–Friday, as weekday numbers (0 = Sunday) and labels. */
const WORKING_DAYS = [
  { day: 1, label: 'Mon' },
  { day: 2, label: 'Tue' },
  { day: 3, label: 'Wed' },
  { day: 4, label: 'Thu' },
  { day: 5, label: 'Fri' },
] as const;
const NOON = 12;
const FIVE_PM = 17;

/** "7a", "12p", "3p". */
export function hourLabel(hour: number): string {
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}${hour < 12 ? 'a' : 'p'}`;
}

/** "10 AM", "3 PM". */
export function hourName(hour: number): string {
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve} ${hour < 12 ? 'AM' : 'PM'}`;
}

const percent = (share: number): string => `${Math.round(share * 100)}%`;
const monthLabel = (month: string): string => {
  const date = new Date(`${month}-15T12:00:00Z`);
  return `${date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })} ’${month.slice(2, 4)}`;
};
/** A month needs this many working days before its rate is worth plotting (the current month starts near zero). */
const MIN_MONTH_WORKING_DAYS = 10;
const plottableMonths = (timing: LeadTiming): LeadTiming['months'] => timing.months.filter((month) => month.workingDays >= MIN_MONTH_WORKING_DAYS);

/** The typical-working-day band with today's bars on top, for the hours we show. */
export function toBandPoints(timing: LeadTiming): BandPoint[] {
  const showToday = timing.today.isWorkingDay;
  return HOURS.map((hour) => {
    const typical = timing.typicalWorkingDay.hours[hour];
    const cumulative = timing.typicalWorkingDay.cumulative[hour];
    return {
      key: String(hour),
      label: hourLabel(hour),
      low: typical?.p25 ?? 0,
      high: typical?.p90 ?? 0,
      center: typical?.mean ?? 0,
      actual: showToday && hour <= timing.today.asOfHour ? (timing.today.byHour[hour] ?? 0) : null,
      details: [`Median ${typical?.p50 ?? 0} · by the end of this hour a typical day has ${cumulative?.p50 ?? 0} (90th pct ${cumulative?.p90 ?? 0})`],
    };
  });
}

/** Weekday × hour counts for the hours we show. */
export function toHeatmapValues(timing: LeadTiming): number[][] {
  return WORKING_DAYS.map(({ day }) => HOURS.map((hour) => timing.weekdayHour[day]?.[hour] ?? 0));
}

function toMonthPoints(timing: LeadTiming, pick: (month: LeadTiming['months'][number]) => number): TrendPoint[] {
  return plottableMonths(timing).map((month) => ({
    key: month.month,
    label: monthLabel(month.month),
    value: pick(month),
    details: [`${month.leads.toLocaleString()} leads over ${month.workingDays} working days`, ...(month.peakHour === null ? [] : [`Busiest hour ${hourName(month.peakHour)}`])],
  }));
}

/**
 * When leads arrive: today against a typical working day, the weekday × hour
 * heatmap, and the month-by-month drift. Always reads the trailing 52 weeks
 * (a season-aligned baseline) rather than the page's range, and refreshes on
 * the page's live cadence so "today so far" keeps moving.
 */
export function LeadTimingPanel(): React.ReactElement {
  const timing = useQuery({
    queryKey: ['lead-timing'],
    queryFn: ({ signal }) => readLeadTiming({}, signal),
    refetchInterval: LIVE_REFRESH_MS,
  });

  if (timing.isError) {
    return (
      <Panel className="mt-4 p-5">
        <ErrorNotice title="Lead timing unavailable" message={errorMessage(timing.error)} />
      </Panel>
    );
  }
  if (timing.isPending) {
    return (
      <Panel className="mt-4 p-5">
        <LoadingState label="Reading a year of lead timestamps…" />
      </Panel>
    );
  }

  const data = timing.data;
  if (data.leads === 0) {
    return (
      <Panel className="mt-4 p-5">
        <EmptyState title="No lead timing yet" message="The profile fills in as the sync brings leads in." />
      </Panel>
    );
  }

  const busiest = data.hours.reduce((best, hour) => (hour.leads > best.leads ? hour : best), data.hours[0]!);
  const byOne = data.hours[NOON]?.cumulativeShare ?? 0;
  const byFive = data.hours[FIVE_PM - 1]?.cumulativeShare ?? 0;
  const typical = data.typicalWorkingDay.total;
  const today = data.today;
  const behind = today.isWorkingDay && today.soFar < today.expectedSoFar.p50;

  return (
    <>
      <Panel className="mt-4 p-5">
        <div className="mb-3">
          <h2 className="text-portal-card-title font-bold text-portal-ink">When leads arrive</h2>
          <p className="text-portal-micro text-portal-muted">
            Counted leads only. Trailing 52 weeks ({data.range.from} – {data.range.to}), {data.workingDays} working days, Eastern time. Percentiles are per hour across
            those days, so a quiet hour is normal and a quiet morning is not.
            {data.excluded > 0 ? ` ${data.excluded.toLocaleString()} integration-batch rows excluded.` : ''}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
          <StatTile
            label={today.isWorkingDay ? `Today so far (by ${hourName(today.asOfHour)})` : 'Today so far'}
            value={today.soFar.toLocaleString()}
            hint={
              today.isWorkingDay
                ? `A typical day has ${today.expectedSoFar.p50} by now (90th pct ${today.expectedSoFar.p90}) · ${behind ? 'behind' : 'on pace'} for the ${today.expectedTotal.p50}-lead median day`
                : 'Not a working day — weekends bring about 1% of leads'
            }
          />
          <StatTile
            label="Typical working day"
            value={typical.p50.toLocaleString()}
            hint={`median · quartiles ${typical.p25}–${typical.p75} · one day in ten over ${typical.p90}`}
          />
          <StatTile label="Busiest hour" value={hourName(busiest.hour)} hint={`${percent(busiest.share)} of the day's leads arrive in this hour`} />
          <StatTile label="In by 1 PM" value={percent(byOne)} hint={`${percent(byFive)} by 5 PM`} />
        </div>
        <div className="mt-5">
          <h3 className="text-portal-small font-bold text-portal-ink">Today against a typical working day</h3>
          <p className="mb-2 text-portal-micro text-portal-muted">Band: 25th–90th percentile per hour · line: mean · bars: today</p>
          <BandChart
            points={toBandPoints(data)}
            title="Leads per hour: today against the typical working day"
            unit="leads"
            bandLabel="Usual range (25th–90th pct)"
            centerLabel="Mean"
            actualLabel="Today"
          />
        </div>
        <div className="mt-5">
          <h3 className="text-portal-small font-bold text-portal-ink">Weekday × hour</h3>
          <p className="mb-2 text-portal-micro text-portal-muted">Leads per cell over the 52 weeks — read across for a day's rhythm, down for how the days differ.</p>
          <HeatmapChart
            rows={WORKING_DAYS.map((day) => day.label)}
            columns={HOURS.map(hourLabel)}
            values={toHeatmapValues(data)}
            title="Leads by weekday and hour"
            unit="leads"
          />
        </div>
      </Panel>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel className="p-5">
          <h3 className="text-portal-card-title font-bold text-portal-ink">Leads per working day, by month</h3>
          <p className="mb-2 text-portal-micro text-portal-muted">Two years, integration batches excluded; a month appears once it has {MIN_MONTH_WORKING_DAYS} working days.</p>
          <TrendChart points={toMonthPoints(data, (month) => month.perWorkingDay)} title="Leads per working day by month" unit="leads per working day" variant="line" height={220} />
        </Panel>
        <Panel className="p-5">
          <h3 className="text-portal-card-title font-bold text-portal-ink">Share of the day before noon</h3>
          <p className="mb-2 text-portal-micro text-portal-muted">Rising means the day is front-loading; the after-5 PM share is in the tooltip.</p>
          <TrendChart
            points={plottableMonths(data).map((month) => ({
              key: month.month,
              label: monthLabel(month.month),
              value: Math.round(month.shareBeforeNoon * 100),
              details: [`${percent(month.shareAfterFive)} after 5 PM`, ...(month.peakHour === null ? [] : [`Busiest hour ${hourName(month.peakHour)}`])],
            }))}
            title="Share of leads arriving before noon, by month"
            unit="% before noon"
            variant="line"
            height={220}
            formatValue={(value) => `${value}%`}
          />
        </Panel>
      </div>
    </>
  );
}
